import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { useTranslation } from 'react-i18next'
import { DocumentViewer } from '../../../Utils/DocumentViewer'

// Previews (and allows downloading) a document. `fetchUrl(doc)` must resolve
// to `{ url }` — callers pass the endpoint appropriate for the document's
// context (e.g. a compliance document vs. a society-level document).
export default function DocumentPreviewDialog({ open, doc, fetchUrl, onClose }) {
  const { t } = useTranslation()
  const [url, setUrl] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !doc) {
      setUrl(null)
      return
    }
    setUrl(null)
    setLoading(true)
    fetchUrl(doc)
      .then((res) => setUrl(res?.url || null))
      .catch(() => setUrl(null))
      .finally(() => setLoading(false))
  }, [open, doc, fetchUrl])

  const handleDownload = async () => {
    if (!url) return
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = blobUrl
    link.download = doc.filename || 'document'
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{doc?.filename}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
        {loading || !url || !doc ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DocumentViewer
            fileUrl={url}
            extension={doc?.filename?.substring(doc.filename.lastIndexOf('.'))}
            height='100%'
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
        <Button
          variant='contained'
          startIcon={<FileDownloadIcon />}
          disabled={!url}
          onClick={handleDownload}
          sx={{
            backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
            borderRadius: window.$brand === 'PRB' ? 0 : 5
          }}
        >
          {t('Download')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
